import React, { Suspense } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

import type { PointsMapProps } from './PointsMap';

// Lazy boundary for the map. PointsMap statically imports maplibre-gl, so importing it
// directly would drag maplibre into whichever chunk references it. The compiled addon
// registry (which renders the GIS_ARTIFACT dashboard widget) is in the eager/initial chunk,
// so a static PointsMap import there put maplibre-gl (~250 kB gzip) in the main bundle.
// Splitting here via React.lazy keeps maplibre in its own async chunk, loaded only when a
// map actually renders. The dynamic import() lives in THIS file (not the registry) on
// purpose: the registry has a security test forbidding dynamic runtime loading paths in its
// own source.
const PointsMap = React.lazy(() => import('./PointsMap'));

/**
 * Drop-in replacement for PointsMap that defers loading the maplibre-backed component to an
 * async chunk. Renders a height-matched loading placeholder while the chunk loads to avoid
 * layout shift, then the real map (which has its own richer loading/empty/error states).
 */
const LazyPointsMap: React.FC<PointsMapProps> = props => {
  const height = props.height ?? 460;
  return (
    <Suspense
      fallback={
        <Box
          sx={{
            height,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            borderRadius: 1,
            border: theme => `1px solid ${theme.palette.divider}`,
            bgcolor: 'action.hover',
          }}
          data-testid={`${props.testId ?? 'points-map'}-chunk-loading`}
        >
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading map…
          </Typography>
        </Box>
      }
    >
      <PointsMap {...props} />
    </Suspense>
  );
};

export default LazyPointsMap;
